import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../../api/httpClient'
import { analyzeTone } from '../../api/toneAnalysisApi'
import { getVideoPreview } from '../../api/videoPreviewApi'
import type { DemoLanguage } from '../../models/DemoAnalysis'
import type { DemoSourceSummary, DemoToneAnalysisErrors } from '../../models/demoToneAnalysis'
import type { SongToneAnalysis } from '../../models/toneAnalysis'
import type { VideoPreview, VideoPreviewStatus } from '../../models/videoPreview'
import { buildToneAnalysisRequest, getDemoSourceSummary } from '../../utils/buildToneAnalysisRequest'
import { isAcceptedDemoAudioFile } from '../../utils/demoAudioSource'
import { parseYouTubePreviewMetadata } from '../../utils/youtubePreviewMetadata'
import { AnalysisWorkflow, type AnalysisWorkflowStep, type AnalysisWorkflowStepId } from './AnalysisWorkflow'
import { ToneAnalysisForm, type ToneAnalysisFormValues } from './ToneAnalysisForm'
import { ToneAnalysisResults } from './ToneAnalysisResults'

type InteractiveDemoProps = {
  language: DemoLanguage
}

type DemoStatus = 'idle' | 'loading' | 'success' | 'error'
type MetadataField = 'songTitle' | 'artist' | 'presetBaseName'

const workflowStepIds: AnalysisWorkflowStepId[] = [
  'preparing',
  'separating',
  'layers',
  'changes',
  'scenes',
  'complete',
]

const workflowLabels: Record<DemoLanguage, Record<AnalysisWorkflowStepId, string>> = {
  en: {
    preparing: 'Preparing audio',
    separating: 'Separating guitar',
    layers: 'Detecting guitar layers',
    changes: 'Detecting tone changes',
    scenes: 'Building tone scenes',
    complete: 'Analysis complete',
  },
  it: {
    preparing: 'Preparazione audio',
    separating: 'Separazione chitarra',
    layers: 'Rilevamento layer',
    changes: 'Rilevamento cambi tone',
    scenes: 'Costruzione scene tone',
    complete: 'Analisi completata',
  },
}

const transparencyCopy: Record<DemoLanguage, string> = {
  en: 'Simulated analysis - powered by the deterministic demo engine.',
  it: 'Analisi simulata - alimentata dal motore demo deterministico.',
}

const visualStepDelayMs = import.meta.env.MODE === 'test' ? 70 : 900
const reducedMotionStepDelayMs = import.meta.env.MODE === 'test' ? 40 : 220
const completionDelayMs = import.meta.env.MODE === 'test' ? 80 : 650
const reducedMotionCompletionDelayMs = import.meta.env.MODE === 'test' ? 80 : 220
const previewDebounceMs = import.meta.env.MODE === 'test' ? 10 : 350

const initialValues: ToneAnalysisFormValues = {
  songTitle: "Nobody's Wife",
  artist: 'Anouk',
  presetBaseName: 'NOBODYSW',
  maximumGuitarLayers: 2,
  sourceMode: 'youtube',
  youtubeUrl: 'https://www.youtube.com/watch?v=tonesignature-demo',
  selectedFile: null,
}

function sleep(durationMs: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timeoutId = window.setTimeout(resolve, durationMs)
    signal.addEventListener('abort', () => {
      window.clearTimeout(timeoutId)
      reject(new DOMException('Aborted', 'AbortError'))
    }, { once: true })
  })
}

function getErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return ''
  }

  if (error instanceof ApiError) {
    return error.message
  }

  return 'Tone analysis could not be completed. Please try again.'
}

function getPreviewErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return ''
  }

  if (error instanceof ApiError) {
    return error.message
  }

  return 'YouTube preview could not be loaded.'
}

function isValidYouTubePreviewUrl(value: string) {
  try {
    const url = new URL(value.trim())
    const host = url.hostname.toLowerCase()

    return host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtu.be'
  } catch {
    return false
  }
}

function createWorkflowSteps(language: DemoLanguage, activeIndex: number, failedIndex: number | null): AnalysisWorkflowStep[] {
  return workflowStepIds.map((id, index) => ({
    id,
    label: workflowLabels[language][id],
    state: failedIndex === index
      ? 'failed'
      : index < activeIndex
        ? 'completed'
        : index === activeIndex
          ? 'active'
          : 'pending',
  }))
}

export function InteractiveDemo({ language }: InteractiveDemoProps) {
  const [values, setValues] = useState<ToneAnalysisFormValues>(initialValues)
  const [errors, setErrors] = useState<DemoToneAnalysisErrors>({})
  const [status, setStatus] = useState<DemoStatus>('idle')
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [failedStepIndex, setFailedStepIndex] = useState<number | null>(null)
  const [analysis, setAnalysis] = useState<SongToneAnalysis | null>(null)
  const [sourceSummary, setSourceSummary] = useState<DemoSourceSummary | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [videoPreview, setVideoPreview] = useState<VideoPreview | null>(null)
  const [videoPreviewStatus, setVideoPreviewStatus] = useState<VideoPreviewStatus>('loading')
  const [videoPreviewError, setVideoPreviewError] = useState('')
  const [videoPreviewRetryKey, setVideoPreviewRetryKey] = useState(0)
  const abortController = useRef<AbortController | null>(null)
  const previewAbortController = useRef<AbortController | null>(null)
  const runId = useRef(0)
  const previewRunId = useRef(0)
  const activeStepIndexRef = useRef(0)
  const lastAutoFilledPreviewUrl = useRef<string | null>(null)
  const metadataFieldEdited = useRef<Record<MetadataField, boolean>>({
    songTitle: false,
    artist: false,
    presetBaseName: false,
  })
  const reduce = useReducedMotion()

  useEffect(() => {
    return () => {
      runId.current += 1
      previewRunId.current += 1
      abortController.current?.abort()
      previewAbortController.current?.abort()
    }
  }, [])

  function applyPreviewMetadata(preview: VideoPreview, url: string) {
    const isDifferentLoadedUrl = lastAutoFilledPreviewUrl.current !== null && lastAutoFilledPreviewUrl.current !== url
    if (isDifferentLoadedUrl) {
      metadataFieldEdited.current = {
        songTitle: false,
        artist: false,
        presetBaseName: false,
      }
    }

    lastAutoFilledPreviewUrl.current = url
    const metadata = parseYouTubePreviewMetadata(preview)

    setValues((current) => ({
      ...current,
      songTitle: metadataFieldEdited.current.songTitle ? current.songTitle : metadata.songTitle,
      artist: metadataFieldEdited.current.artist ? current.artist : metadata.artist,
      presetBaseName: metadataFieldEdited.current.presetBaseName ? current.presetBaseName : metadata.presetBaseName,
    }))
  }

  useEffect(() => {
    previewRunId.current += 1
    const currentPreviewRunId = previewRunId.current
    previewAbortController.current?.abort()

    if (values.sourceMode !== 'youtube') {
      return
    }

    const url = values.youtubeUrl.trim()

    if (!url || !isValidYouTubePreviewUrl(url)) {
      return
    }

    const nextPreviewAbortController = new AbortController()
    previewAbortController.current = nextPreviewAbortController

    const timeoutId = window.setTimeout(async () => {
      try {
        const preview = await getVideoPreview(url, nextPreviewAbortController.signal)
        if (previewRunId.current !== currentPreviewRunId) {
          return
        }

        applyPreviewMetadata(preview, url)
        setVideoPreview(preview)
        setVideoPreviewStatus('loaded')
      } catch (error) {
        if (previewRunId.current !== currentPreviewRunId) {
          return
        }

        const message = getPreviewErrorMessage(error)
        if (!message) {
          return
        }

        setVideoPreviewError(message)
        setVideoPreviewStatus('error')
      }
    }, previewDebounceMs)

    return () => {
      window.clearTimeout(timeoutId)
      nextPreviewAbortController.abort()
    }
  }, [values.sourceMode, values.youtubeUrl, videoPreviewRetryKey])

  function validate() {
    const nextErrors: DemoToneAnalysisErrors = {}

    if (!values.songTitle.trim()) {
      nextErrors.songTitle = 'Song title is required.'
    }

    if (!values.presetBaseName.trim()) {
      nextErrors.presetBaseName = 'Preset base name is required.'
    }

    if (values.maximumGuitarLayers !== 1 && values.maximumGuitarLayers !== 2) {
      nextErrors.maximumGuitarLayers = 'Choose rhythm only or rhythm plus lead.'
    }

    if (values.sourceMode === 'youtube') {
      if (!values.youtubeUrl.trim()) {
        nextErrors.youtubeUrl = 'Enter a YouTube URL for the demo.'
      } else if (!isValidYouTubePreviewUrl(values.youtubeUrl)) {
        nextErrors.youtubeUrl = 'Enter a valid YouTube URL for the demo.'
      } else if (videoPreviewStatus !== 'loaded' || !videoPreview) {
        nextErrors.youtubeUrl = 'Load the YouTube preview before analysis.'
      }
    }

    if (values.sourceMode === 'file') {
      if (!values.selectedFile) {
        nextErrors.selectedFile = 'Choose an audio file for the demo.'
      } else if (!isAcceptedDemoAudioFile(values.selectedFile)) {
        nextErrors.selectedFile = 'Choose an MP3, WAV, M4A or FLAC file.'
      }
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  function clearSourceErrors() {
    setErrors((current) => {
      const remaining = { ...current }
      delete remaining.youtubeUrl
      delete remaining.selectedFile
      return remaining
    })
  }

  function updateActiveStepIndex(index: number) {
    activeStepIndexRef.current = index
    setActiveStepIndex(index)
  }

  async function runMinimumVisualWorkflow(signal: AbortSignal, currentRunId: number) {
    const delay = reduce ? reducedMotionStepDelayMs : visualStepDelayMs

    for (let index = 0; index < workflowStepIds.length - 1; index += 1) {
      if (runId.current !== currentRunId) {
        return
      }

      updateActiveStepIndex(index)
      await sleep(delay, signal)
    }

    updateActiveStepIndex(workflowStepIds.length - 2)
  }

  async function handleAnalyze() {
    if (status === 'loading' || !validate()) {
      return
    }

    abortController.current?.abort()
    const nextAbortController = new AbortController()
    const currentRunId = runId.current + 1
    runId.current = currentRunId
    abortController.current = nextAbortController
    setStatus('loading')
    updateActiveStepIndex(0)
    setFailedStepIndex(null)
    setErrorMessage('')
    setAnalysis(null)
    setSourceSummary(getDemoSourceSummary(values))

    const requestPromise = analyzeTone(buildToneAnalysisRequest(values), nextAbortController.signal)
    const visualPromise = runMinimumVisualWorkflow(nextAbortController.signal, currentRunId)

    try {
      const [response] = await Promise.all([requestPromise, visualPromise])
      if (runId.current !== currentRunId) {
        return
      }

      updateActiveStepIndex(workflowStepIds.length - 1)
      await sleep(reduce ? reducedMotionCompletionDelayMs : completionDelayMs, nextAbortController.signal)

      if (runId.current !== currentRunId) {
        return
      }

      updateActiveStepIndex(workflowStepIds.length)
      setAnalysis(response)
      setStatus('success')
    } catch (error) {
      nextAbortController.abort()

      if (runId.current !== currentRunId) {
        return
      }

      const message = getErrorMessage(error)
      if (!message) {
        return
      }

      setFailedStepIndex((current) => current ?? activeStepIndexRef.current)
      setErrorMessage(message)
      setStatus('error')
    }
  }

  function handleAnalyzeAnother() {
    runId.current += 1
    abortController.current?.abort()
    setStatus('idle')
    updateActiveStepIndex(0)
    setFailedStepIndex(null)
    setAnalysis(null)
    setErrorMessage('')
  }

  function resetPreviewForValues(nextValues: ToneAnalysisFormValues) {
    previewRunId.current += 1
    previewAbortController.current?.abort()
    setVideoPreview(null)
    setVideoPreviewError('')
    setVideoPreviewStatus(
      nextValues.sourceMode === 'youtube' && isValidYouTubePreviewUrl(nextValues.youtubeUrl)
        ? 'loading'
        : 'idle',
    )
  }

  function handleFormChange(nextValues: ToneAnalysisFormValues) {
    if (nextValues.sourceMode !== values.sourceMode || nextValues.youtubeUrl !== values.youtubeUrl) {
      resetPreviewForValues(nextValues)
    }

    setValues(nextValues)
  }

  function handleMetadataFieldEdit(field: MetadataField) {
    metadataFieldEdited.current[field] = true
  }

  function handleRetryPreview() {
    resetPreviewForValues(values)
    setVideoPreviewRetryKey((current) => current + 1)
  }

  const animationProps = reduce
    ? {}
    : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, transition: { duration: 0.28 } }
  const workflowSteps = createWorkflowSteps(language, activeStepIndex, failedStepIndex)
  const hasPreviewableYouTubeUrl = values.sourceMode === 'youtube' && isValidYouTubePreviewUrl(values.youtubeUrl)
  const canAnalyze = values.sourceMode === 'file' || !hasPreviewableYouTubeUrl || videoPreviewStatus === 'loaded'

  return <div className="ts-demo-shell">
    <AnimatePresence mode="wait">
      {(status === 'idle' || status === 'loading' || status === 'error') && <motion.div key="analysis-form" className="ts-demo-input" {...animationProps}>
        <ToneAnalysisForm
          values={values}
          errors={errors}
          preview={videoPreview}
          previewStatus={videoPreviewStatus}
          previewError={videoPreviewError}
          isLoading={status === 'loading'}
          canAnalyze={canAnalyze}
          onChange={handleFormChange}
          onClearSourceErrors={clearSourceErrors}
          onMetadataFieldEdit={handleMetadataFieldEdit}
          onRetryPreview={handleRetryPreview}
          onSubmit={handleAnalyze}
        />
        {(status === 'loading' || status === 'error') && <AnalysisWorkflow
          steps={workflowSteps}
          transparency={transparencyCopy[language]}
        />}
        {status === 'error' && <div className="ts-analysis-error" role="alert">
          {errorMessage}
        </div>}
        <small className="ts-analysis-transparency">
          {transparencyCopy[language]}
        </small>
      </motion.div>}

      {status === 'success' && analysis && <motion.div key="analysis-result" className="ts-demo-result api-backed" {...animationProps}>
        <ToneAnalysisResults
          analysis={analysis}
          sourceSummary={sourceSummary}
          onAnalyzeAnother={handleAnalyzeAnother}
        />
      </motion.div>}
    </AnimatePresence>
  </div>
}
